import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextListIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/list-icon';
import { RichTextListOrderedIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/list-ordered-icon';
import { RichTextListTodoIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/list-todo-icon';
import { richTextIsNodeInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';

('use client');

export type RichTextListType = 'bulletList' | 'orderedList' | 'taskList';

export interface RichTextListOption {
  label: string;
  type: RichTextListType;
  icon: React.ElementType;
}

export interface RichTextListButtonProps
  extends Omit<RichTextButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * The type of list to toggle.
   */
  type: RichTextListType;
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Whether the button should hide when the list is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export const richTextListOptions: RichTextListOption[] = [
  {
    label: 'Bullet List',
    type: 'bulletList',
    icon: RichTextListIcon,
  },
  {
    label: 'Ordered List',
    type: 'orderedList',
    icon: RichTextListOrderedIcon,
  },
  {
    label: 'Task List',
    type: 'taskList',
    icon: RichTextListTodoIcon,
  },
];

export const richTextListShortcutKeys: Record<RichTextListType, string> = {
  bulletList: 'Ctrl-Shift-8',
  orderedList: 'Ctrl-Shift-7',
  taskList: 'Ctrl-Shift-9',
};

export function richTextCanToggleList(
  editor: Editor | null,
  type: RichTextListType,
): boolean {
  if (!editor) {
    return false;
  }

  switch (type) {
    case 'bulletList':
      return editor.can().toggleBulletList();
    case 'orderedList':
      return editor.can().toggleOrderedList();
    case 'taskList':
      return editor.can().toggleList('taskList', 'taskItem');
    default:
      return false;
  }
}

export function richTextIsListActive(
  editor: Editor | null,
  type: RichTextListType,
): boolean {
  if (!editor) return false;

  switch (type) {
    case 'bulletList':
      return editor.isActive('bulletList');
    case 'orderedList':
      return editor.isActive('orderedList');
    case 'taskList':
      return editor.isActive('taskList');
    default:
      return false;
  }
}

export function richTextToggleList(
  editor: Editor | null,
  type: RichTextListType,
): void {
  if (!editor) return;

  switch (type) {
    case 'bulletList':
      editor.chain().focus().toggleBulletList().run();
      break;
    case 'orderedList':
      editor.chain().focus().toggleOrderedList().run();
      break;
    case 'taskList':
      editor.chain().focus().toggleList('taskList', 'taskItem').run();
      break;
  }
}

export function richTextGetListOption(
  type: RichTextListType,
): RichTextListOption | undefined {
  return richTextListOptions.find((option) => option.type === type);
}

export function richTextShouldShowListButton(params: {
  editor: Editor | null;
  type: RichTextListType;
  hideWhenUnavailable: boolean;
  listInSchema: boolean;
}): boolean {
  const { editor, type, hideWhenUnavailable, listInSchema } = params;

  if (!listInSchema || !editor) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (
      isNodeSelection(editor.state.selection) ||
      !richTextCanToggleList(editor, type)
    ) {
      return false;
    }
  }

  return true;
}

export function useRichTextListState(
  editor: Editor | null,
  type: RichTextListType,
) {
  const listInSchema = richTextIsNodeInSchema(type, editor);
  const listOption = richTextGetListOption(type);
  const isActive = richTextIsListActive(editor, type);
  const shortcutKey = richTextListShortcutKeys[type];

  return {
    listInSchema,
    listOption,
    isActive,
    shortcutKey,
  };
}

export const RichTextListButton = React.forwardRef<
  HTMLButtonElement,
  RichTextListButtonProps
>(
  (
    {
      editor: providedEditor,
      type,
      hideWhenUnavailable = false,
      className = '',
      onClick,
      text,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const editor = useRichTextTiptapEditor(providedEditor);
    const { listInSchema, listOption, isActive, shortcutKey } =
      useRichTextListState(editor, type);

    const Icon = listOption?.icon || RichTextListIcon;

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && editor) {
          richTextToggleList(editor, type);
        }
      },
      [onClick, editor, type],
    );

    const show = React.useMemo(() => {
      return richTextShouldShowListButton({
        editor,
        type,
        hideWhenUnavailable,
        listInSchema,
      });
    }, [editor, type, hideWhenUnavailable, listInSchema]);

    if (!show || !editor || !editor.isEditable) {
      return null;
    }

    return (
      <RichTextButton
        type="button"
        className={className.trim()}
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        role="button"
        tabIndex={-1}
        aria-label={listOption?.label || type}
        aria-pressed={isActive}
        tooltip={listOption?.label || type}
        shortcutKeys={shortcutKey}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children || (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </RichTextButton>
    );
  },
);

RichTextListButton.displayName = 'ListButton';

export default RichTextListButton;
